import type { SecurityClearance } from "@fairfleet/geotab";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { ROOT_SECURITY_GROUP_ID } from "@/constants/geotabGroupIds";
import { RESULTS_LIMIT_GROUP } from "@/constants/resultsLimits";

export function securityClearancesQueryKey(activeSessionId: string | null | undefined) {
  return ["securityClearances", activeSessionId] as const;
}

/**
 * Fetches the security root group plus every descendant clearance.
 * This is an app-wide shared query; pass `select` to derive a view of its cache entry.
 * Errors propagate to the caller.
 *
 * Retries once so a transient failure does not leave permission-gated UI hidden,
 * but not three times (TanStack's default), which would delay a real failure by ~7 s.
 */
export function useSecurityClearances<T = SecurityClearance[]>(
  select?: (c: SecurityClearance[]) => T,
): UseQueryResult<T, Error> {
  const { geotabClient, activeSessionId, clearGeotabClient } = useGeotabClient();

  return useQuery<SecurityClearance[], Error, T>({
    queryKey: securityClearancesQueryKey(activeSessionId),
    queryFn: async ({ signal }) => {
      try {
        const groups = await geotabClient.get(
          "Group",
          { id: ROOT_SECURITY_GROUP_ID },
          RESULTS_LIMIT_GROUP,
          signal,
        );
        return (groups ?? []) as SecurityClearance[];
      } catch (e) {
        if (isInvalidSession(e) && activeSessionId) {
          clearGeotabClient(activeSessionId);
          console.log("Session expired, clearing Geotab client");
        }
        throw e;
      }
    },
    select,
    // One retry for a transient failure; none after an expired session, which a retry through
    // the cleared client cannot fix.
    retry: (failureCount, error) => failureCount < 1 && !isInvalidSession(error),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

function isInvalidSession(error: unknown): boolean {
  return error instanceof Error && error.message.includes("Invalid session");
}
